import areEqual from "fbjs/lib/areEqual";

import { iterateEntries, IterUtils } from "../../../../../Common/IterableUtils";
import { ModelEvent } from "../EventManagement/ModelEvents/ModelEvent";
import { ServerChangeSet } from "../../../../../Common/CandyApiClient/Types/ChangeSet/ServerChangeSet";
import { PathTokens } from "../../../../../Common/ModelPath/ModelPath";
import { isNotNullOrUndefined } from "../../../../../Common/TypingUtils";

const AbsentValue = Symbol("AbsentValue");

interface ChangeSetInternal {
    added: { [path: string]: string };
    changed: { [path: string]: string };
    removed: { [path: string]: string };
    oldValues: { [path: string]: string | typeof AbsentValue };
}

export class ChangeSet {
    private readonly changeSet: ChangeSetInternal;
    private readonly includeOldValues: boolean;

    public constructor(includeOldValues: boolean) {
        this.includeOldValues = includeOldValues;
        this.changeSet = {
            added: {},
            changed: {},
            removed: {},
            oldValues: {},
        };
    }

    public appendEventInfo(eventArgs: ModelEvent): void {
        const value = eventArgs.value === undefined ? null : eventArgs.value;
        const oldValue = eventArgs.oldValue === undefined ? AbsentValue : eventArgs.oldValue;
        const path = eventArgs.fullPath?.toLegacyPath(false) ?? eventArgs.path.toLegacyPath();

        const requiredToSave = !(
            PathTokens.isInstanceToken(IterUtils.last(eventArgs.path.getPathParts())) && eventArgs.name == undefined
        );

        if (requiredToSave) {
            if (eventArgs.type === "change") {
                delete this.changeSet.removed[path];
                this.changeSet.changed[path] = value;
            } else if (eventArgs.type === "remove") {
                delete this.changeSet.added[path];
                delete this.changeSet.changed[path];
                this.changeSet.removed[path] = value;
            }

            if (this.includeOldValues) {
                const prevOldValue = this.changeSet.oldValues[path];
                this.changeSet.oldValues[path] =
                    prevOldValue == undefined && prevOldValue !== AbsentValue ? oldValue : prevOldValue;
            }
        }
    }

    public canBeMerged(event: ModelEvent) {
        const path = event.fullPath?.toLegacyPath() ?? event.path.toLegacyPath();
        const isConflictedChange =
            event.name === "children" &&
            event.type == "change" &&
            ((this.changeSet.changed[path] != undefined && !areEqual(this.changeSet.changed[path], event.value)) ||
                this.changeSet.removed[path] != undefined);

        return !isConflictedChange;
    }

    public isEmpty(): boolean {
        const data = this.toServerChangeSet();
        return data.added.length === 0 && data.removed.length === 0 && !Object.keys(data.changed).length;
    }

    public toServerChangeSet(): ServerChangeSet {
        const oldValuesWithoutAbsent = Object.fromEntries(
            Object.entries(this.changeSet.oldValues)
                .map(([key, value]) => (value !== AbsentValue ? [key, value] : undefined))
                .filter(isNotNullOrUndefined)
        );
        return {
            added: Object.keys(this.changeSet.added),
            changed: this.changeSet.changed,
            removed: Object.keys(this.changeSet.removed),
            oldValues: oldValuesWithoutAbsent,
        };
    }

    public prependWithChangeSet(source: ChangeSet): void {
        const targetChangeSet = this.changeSet;
        const sourceChangeSet = source.changeSet;

        for (const [path, sourceAddedKey] of iterateEntries(sourceChangeSet.added)) {
            if (targetChangeSet.removed[path] != undefined) {
                continue;
            }
            targetChangeSet.added[path] = sourceAddedKey;
        }

        for (const [path, sourceChangedValue] of iterateEntries(sourceChangeSet.changed)) {
            if (targetChangeSet.removed[path] != undefined) {
                continue;
            }
            targetChangeSet.changed[path] = sourceChangedValue;
        }

        for (const [path, sourceChangedValue] of iterateEntries(sourceChangeSet.oldValues)) {
            if (targetChangeSet.removed[path] != undefined) {
                continue;
            }
            if (this.includeOldValues) {
                if (targetChangeSet.oldValues[path] == undefined) {
                    targetChangeSet.oldValues[path] = sourceChangedValue;
                }
            }
        }

        for (const [path, sourceRemovedKey] of iterateEntries(sourceChangeSet.removed)) {
            if (targetChangeSet.added[path] != undefined) {
                continue;
            }
            if (targetChangeSet.changed[path] != undefined) {
                continue;
            }
            targetChangeSet.removed[path] = sourceRemovedKey;
        }
    }
}
